/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { Entity } from '@backstage/catalog-model';
import { useViewAs } from '../viewAs/ViewAsContext';
import {
  useOwnedComponents,
  useScope,
  filterByScopes,
  effectiveScopeRefs,
  serviceFacets,
  filterServices,
} from './homeScope';

export type HomeScopeState = {
  loading: boolean;
  error?: Error;
  hasValue: boolean;
  allEntities: Entity[];
  teamRefs: string[];
  teamsByRef: Record<string, Entity>;
  currentUser?: Entity;
  childrenMap: Record<string, string[]>;
  directRefs: string[];
  scopes: string[];
  setScopes: (next: string[]) => void;
  subTeams: boolean;
  setSubTeams: (next: boolean) => void;
  hasSubTeams: boolean;
  scopedEntities: Entity[];
  effectiveOwnerRefs: string[];
  typeFilter: string | null;
  setTypeFilter: (v: string | null) => void;
  lifecycleFilter: string | null;
  setLifecycleFilter: (v: string | null) => void;
  typeFacets: ReturnType<typeof serviceFacets>;
  lifecycleFacets: ReturnType<typeof serviceFacets>;
  filteredServices: Entity[];
  servicesViewAllHref: string;
};

const HomeScopeContext = createContext<HomeScopeState | null>(null);

export const useHomeScope = (): HomeScopeState => {
  const ctx = useContext(HomeScopeContext);
  if (!ctx) {
    throw new Error('useHomeScope must be used within a HomeScopeProvider');
  }
  return ctx;
};

export const HomeScopeProvider = ({ children }: { children: React.ReactNode }) => {
  const { viewAsRef, viewingAs } = useViewAs();
  const { value, loading, error } = useOwnedComponents(viewAsRef);
  const allEntities = value?.entities ?? [];
  const teamRefs = value?.teamRefs ?? [];
  const teamsByRef = value?.teamsByRef ?? {};
  const currentUser = value?.currentUser;
  const childrenMap = value?.childrenMap ?? {};
  const directRefs = value?.directRefs ?? [];
  const persisted = useScope(teamRefs);
  const [lensScopes, setLensScopes] = useState<string[]>([]);
  const [lensSubTeams, setLensSubTeams] = useState(false);
  const scopes = viewingAs
    ? lensScopes.filter(r => teamRefs.includes(r))
    : persisted.scopes;
  const setScopes = viewingAs ? setLensScopes : persisted.setScopes;
  const subTeams = viewingAs ? lensSubTeams : persisted.subTeams;
  const setSubTeams = viewingAs ? setLensSubTeams : persisted.setSubTeams;
  useEffect(() => {
    setLensScopes([]);
    setLensSubTeams(false);
  }, [viewAsRef]);
  const scopedEntities = useMemo(
    () => filterByScopes(allEntities, scopes, { directRefs, childrenMap, subTeams }),
    [allEntities, scopes, directRefs, childrenMap, subTeams],
  );
  const effectiveOwnerRefs = useMemo(
    () => effectiveScopeRefs(scopes, { directRefs, childrenMap, subTeams }),
    [scopes, directRefs, childrenMap, subTeams],
  );
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [lifecycleFilter, setLifecycleFilter] = useState<string | null>(null);
  const typeFacets = useMemo(() => serviceFacets(scopedEntities, 'type'), [scopedEntities]);
  const lifecycleFacets = useMemo(
    () => serviceFacets(scopedEntities, 'lifecycle'),
    [scopedEntities],
  );
  const filteredServices = useMemo(
    () => filterServices(scopedEntities, typeFilter, lifecycleFilter),
    [scopedEntities, typeFilter, lifecycleFilter],
  );
  const servicesViewAllHref = useMemo(() => {
    const owners = effectiveOwnerRefs
      .map(r => `filters[owners]=${encodeURIComponent(r)}`)
      .join('&');
    const ownersClause = owners ? `filters[user]=all&${owners}` : 'filters[user]=owned';
    const facetClause = [
      typeFilter ? `filters[type]=${encodeURIComponent(typeFilter)}` : '',
      lifecycleFilter ? `filters[lifecycles]=${encodeURIComponent(lifecycleFilter)}` : '',
    ].filter(Boolean).join('&');
    return `/catalog?filters[kind]=component&${ownersClause}${facetClause ? `&${facetClause}` : ''}`;
  }, [effectiveOwnerRefs, typeFilter, lifecycleFilter]);
  const hasSubTeams = useMemo(
    () => teamRefs.some(ref => (childrenMap[ref] ?? []).length > 0),
    [teamRefs, childrenMap],
  );

  const state: HomeScopeState = {
    loading,
    error,
    hasValue: !!value,
    allEntities,
    teamRefs,
    teamsByRef,
    currentUser,
    childrenMap,
    directRefs,
    scopes,
    setScopes,
    subTeams,
    setSubTeams,
    hasSubTeams,
    scopedEntities,
    effectiveOwnerRefs,
    typeFilter,
    setTypeFilter,
    lifecycleFilter,
    setLifecycleFilter,
    typeFacets,
    lifecycleFacets,
    filteredServices,
    servicesViewAllHref,
  };

  return (
    <HomeScopeContext.Provider value={state}>{children}</HomeScopeContext.Provider>
  );
};
